import { FunctionComponent, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom'

import { Typography, Card, CardContent, Stack, Button, Dialog, DialogTitle, Container } from '@mui/material';

import HorizontalLinearStepper from 'components/common/HorizontalLinearStepper';
import MessageCard from 'components/collection/wizard/MessageCard';
import ScheduleCard from 'components/collection/wizard/ScheduleCard';
import PaymentCard from 'components/collection/wizard/PaymentCard';
import localAuth from 'model/auth/AuthStore';
import { ErrorContainer } from 'components/common/ErrorContainer';

import { CollectionModel, emptyCollection } from 'model/collection/CollectionModels';
import useCollections, { CollectionState } from 'model/collection/CollectionStore';
import { Loading } from 'components/common/Loading';
import { updateStatus, del } from 'access/CollectingService';

import { Toolbar } from 'components/common/Toolbar';
import Grid from '@mui/material/Grid2'
import { useApi } from 'hooks/useApi';

import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import { DELETE, PAUSE, PLAY, EXCLAMATION } from 'assets/Icons';
import { BACKEND_ENDPOINT, log } from 'utils/Common';

export interface CollectionWizardProps {
  onFinished?: () => void;
  saveLocally?: boolean;
  finishedText: string;
  title?: string;
  subtitle?: string;
  firstBackText: string;
  cancelled: () => void;
  id?: string;
}


export const CollectionWizard: FunctionComponent<CollectionWizardProps> = 
  ({saveLocally = false, finishedText, title, subtitle, firstBackText, cancelled, id, onFinished}) => {
  const navigate = useNavigate();
    
  const accessToken: string = localAuth((state) => state.authToken);
  const { request, loading, error } = useApi({ initialLoading: (id ? true : false) });
  const collectionState = useCollections((state) => state.collection);
  const setCollection = useCollections((state) => state.setCollection);
  const [status, setStatus] = useState("active")
  const [isDeleteModalopen, setIsDeleteModalOpen] = useState(false)
  
  const [loadingStatus, setLoadingStatus] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);

  const [noCollection, setNoCollection] = useState<boolean>(false);

  const [activeStep, setActiveStep] = useState(0);
  const currentEndDate = useCollections((state: CollectionState) => state.endDateCache);
  const setEndDateCache = useCollections((state: CollectionState) => state.setEndDateCache);

  const handleNext = () => {
    setActiveStep(activeStep + 1);
  }

  const handleBack = () => {
    setActiveStep(activeStep - 1);
  }
  
  const toggleDeletionModal = () => {
    setIsDeleteModalOpen(true)
  }

  const steps = [{
    stepTitle: "Payment",
    component: <PaymentCard
      canCancel={!loadingStatus}
      isNew={id ? false : true}
      nextText='Next'
      next={handleNext} 
      back={cancelled} 
      backText={firstBackText}/>
  }, {
    stepTitle: "Schedule",
    component: <ScheduleCard 
      currentEndDate={currentEndDate} 
      isNew={id ? false : true}
      nextText='Next' 
      next={handleNext} 
      back={handleBack} 
      backText="Back"/>
  }, {
    stepTitle: "Message",
    component: <MessageCard 
      accessToken={accessToken}
      saveLocally={saveLocally}
      isNew={id ? false : true}
      nextText={finishedText} 
      setSaving={setSaving}
      back={handleBack} 
      next={() => { if (onFinished) onFinished(); else navigate("/"); }}
      backText="Back"/>
  }]

  const confirmDeletion = () => {
    del(accessToken, id)
      .then((data)=>navigate(-1))
  }
  
  const statusClicked = () => {
    setLoadingStatus(true);
    const newStatus = collectionState.status === "paused" ? "active" : "paused";
    updateStatus(accessToken, collectionState.id, newStatus)
      .catch((error) => {
        log("error");
        log(error);
        setLoadingStatus(false);
      }).then(data => {
        log("updated");
        log(data);
        collectionState.status = data as string;
        setStatus(data as string)
        setLoadingStatus(false);
      });
  }

  const toolbarItems = [
    <Button color="inherit" onClick={()=>statusClicked()} key="1" disabled={saving || status === "expired"}>
      {
        status === "paused" ?
        <>{PLAY(21)}&nbsp;&nbsp;Activate</> : 
        status === "active" ?
        <>{PAUSE(21)}&nbsp;&nbsp;Pause</> :
        <>{EXCLAMATION(21)}&nbsp;&nbsp;Expired</> 
      }
    </Button>,
    <Button color="inherit" onClick={toggleDeletionModal} key="2" disabled={saving}>{DELETE(21)}&nbsp;&nbsp;Delete</Button>
  ]

    useEffect(() => {
      setCollection(emptyCollection());
      const loadCollection = async () => {
        try {
          const data = await request<CollectionModel[]>(() => 
            fetch(BACKEND_ENDPOINT + `collecting?id=${id}`, {
              headers: {
                'Content-Type': 'application/json', 
                'Accept': 'application/json', 
                'Authorization': `Bearer ${accessToken}`
              }
            })
          );
          if (!data || data.length === 0) {
            setNoCollection(true);
            return
          }
          log("data:" + data);
          setCollection(data[0]);
          setEndDateCache(data[0].endDate);
          setStatus(data[0].status);
        } catch (error) {
          log("Failed to fetch collections: " + error);
        }
      };

      if (id) loadCollection();
    }, [accessToken, id]);

  if (noCollection) {
    return (
      <Container maxWidth="md">
        <Stack spacing={1}>
          <ErrorContainer errorToolbar={<div style={{paddingLeft: "16px", paddingTop: "20px"}}>
            <Grid container>
              <Grid display="flex" alignItems="center">
                <Typography variant='h6'><AttachMoneyIcon className='toolbar-mui-icon'/>COLLECTION</Typography>
              </Grid>
              <Grid size="grow"></Grid>
            </Grid></div>} height='71.33px'/>
          <ErrorContainer text='Collection not found' height='390px'/>
        </Stack>
      </Container>
    )
  }

  if (loading) {
    return (
      <Container maxWidth="md">
        <Stack spacing={1}><Loading toolbarLoading={<div style={{paddingLeft: "16px", paddingTop: "18px"}}>
          <Grid container>
            <Grid display="flex" alignItems="center">
              <Typography variant='h6'><AttachMoneyIcon className='toolbar-mui-icon'/>COLLECTION</Typography>
            </Grid>
            <Grid size="grow"></Grid>
          </Grid></div>} variant='walker' height='68px'/>
          <Loading variant='walker' height='690px'/>
        </Stack>
      </Container>
    )
  }

  if (error) {
    return (
      <Container maxWidth="md">
        <Stack spacing={1}>
          <ErrorContainer errorToolbar={<div style={{paddingLeft: "16px", paddingTop: "20px"}}>
            <Grid container>
              <Grid display="flex" alignItems="center">
                <Typography variant='h6'><AttachMoneyIcon className='toolbar-mui-icon'/>COLLECTION</Typography>
              </Grid>
              <Grid size="grow"></Grid>
            </Grid></div>} height='71.33px'/>
          <ErrorContainer text='An error occured while trying to fetch the collections. Please try again later.' height='390px'/>
        </Stack>
      </Container>
    )
  }

  return (<>
    <Stack spacing={1} sx={{mb: "1em"}}>
      {!id?<></>:
        <Toolbar title='COLLECTION' 
        icon={<AttachMoneyIcon className='toolbar-mui-icon'/>} 
        items={toolbarItems}/>
      }
      <Card variant='outlined'>
        <CardContent>
          {!title?<></>:<Typography variant='h5' style={{textAlign:"center", fontSize: "1.8em"}}>{title}</Typography>}
          {!subtitle?<></>:<Typography variant='body1' style={{textAlign:"center", marginBottom: "70px"}}>{subtitle}</Typography>}
          
          <HorizontalLinearStepper steps={steps} activeStep={activeStep} />
    
        </CardContent>
      </Card>
    </Stack>

    <Dialog onClose={()=> setIsDeleteModalOpen(false)} open={isDeleteModalopen}>
      <DialogTitle>Confirm deletion</DialogTitle>
      <Grid container>
        <Grid><Button onClick={()=> setIsDeleteModalOpen(false)}>No</Button></Grid>
        <Grid size="grow"></Grid>
        <Grid><Button onClick={confirmDeletion}>Yes</Button></Grid>
      </Grid>
    </Dialog>
  </>);
}
export default CollectionWizard;